import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SellerAuthModal from './components/SellerAuthModal';
import UploadProductModal from './components/UploadProductModal';
import ShipOrderModal from './components/ShipOrderModal';
import { sellerMockData } from '@/mocks/shop';

type Tab = 'overview' | 'products' | 'orders' | 'withdraw';

const statusLabel: Record<string, { label: string; color: string }> = {
  pending: { label: '待发货', color: 'bg-amber-100 text-amber-700' },
  shipped: { label: '已发货', color: 'bg-blue-100 text-blue-700' },
  delivered: { label: '已签收', color: 'bg-green-100 text-green-700' },
};

export default function SellerDashboardPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAuth, setShowAuth] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showUpload, setShowUpload] = useState(false);
  const [shipOrderId, setShipOrderId] = useState<string | null>(null);
  const [orders, setOrders] = useState(sellerMockData.recentOrders);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState<'wechat' | 'alipay' | 'bank'>('wechat');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  const handleLogin = () => {
    setIsLoggedIn(true);
    setShowAuth(false);
  };

  const handleShip = (orderId: string, trackingNo: string) => {
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'shipped', trackingNo } : o));
    setShipOrderId(null);
  };

  const handleWithdraw = () => {
    setWithdrawSuccess(true);
    setTimeout(() => setWithdrawSuccess(false), 3000);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#faf9f7]">
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center px-4">
          <SellerAuthModal onSuccess={handleLogin} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-700 py-8 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl">
                <i className="ri-store-3-line text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-white font-black text-xl">{sellerMockData.name}</h1>
                <p className="text-white/70 text-sm">卖家工作台 · 已认证商家</p>
              </div>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-bold rounded-full hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-upload-cloud-line"></i>上传商品
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: '总销售额', value: `¥${sellerMockData.totalRevenue.toLocaleString()}`, icon: 'ri-money-cny-circle-line', color: 'text-red-600', bg: 'bg-red-50' },
              { label: '可提现余额', value: `¥${sellerMockData.pendingWithdraw.toLocaleString()}`, icon: 'ri-wallet-3-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: '总订单数', value: sellerMockData.totalOrders.toLocaleString(), icon: 'ri-shopping-bag-line', color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: '在售商品', value: sellerMockData.totalProducts, icon: 'ri-box-3-line', color: 'text-orange-600', bg: 'bg-orange-50' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl p-5">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${s.bg} mb-3`}>
                  <i className={`${s.icon} ${s.color} text-xl`}></i>
                </div>
                <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-full px-1 py-1 mb-8 w-fit">
            {([
              { id: 'overview', label: '数据概览', icon: 'ri-bar-chart-line' },
              { id: 'products', label: '我的商品', icon: 'ri-box-3-line' },
              { id: 'orders', label: '订单管理', icon: 'ri-shopping-bag-line' },
              { id: 'withdraw', label: '提现', icon: 'ri-bank-card-line' },
            ] as { id: Tab; label: string; icon: string }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === t.id ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <i className={t.icon}></i>{t.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-black text-gray-900 mb-5">近7天销售趋势</h3>
                <div className="flex items-end gap-3 h-40">
                  {[1200, 980, 1450, 1100, 1680, 1320, 1890].map((v, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-gray-400">¥{(v / 100).toFixed(0)}k</span>
                      <div
                        className="w-full bg-red-500 rounded-t-lg transition-all"
                        style={{ height: `${(v / 1890) * 100}%` }}
                      ></div>
                      <span className="text-xs text-gray-400">{['周一', '周二', '周三', '周四', '周五', '周六', '周日'][i]}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-black text-gray-900 mb-5">热销商品 TOP 3</h3>
                <div className="flex flex-col gap-4">
                  {[
                    { name: '晨光多功能学生文具套装', sales: 3420, revenue: 136458 },
                    { name: '英雄钢笔礼盒套装', sales: 1820, revenue: 123760 },
                    { name: '小学生铅笔盒文具盒', sales: 5670, revenue: 112833 },
                  ].map((item, i) => (
                    <div key={item.name} className="flex items-center gap-4">
                      <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-black flex-shrink-0 ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : 'bg-orange-300 text-white'}`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                        <p className="text-xs text-gray-400">已售 {item.sales} 件</p>
                      </div>
                      <p className="text-sm font-bold text-red-600 whitespace-nowrap">¥{item.revenue.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h3 className="font-black text-gray-900">商品列表</h3>
                <button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>上传新商品
                </button>
              </div>
              <div className="divide-y divide-gray-50">
                {[
                  { name: '晨光多功能学生文具套装', price: 39.9, stock: 500, sales: 3420, status: 'active' },
                  { name: '英雄钢笔礼盒套装', price: 68.0, stock: 300, sales: 1820, status: 'active' },
                  { name: '小学生铅笔盒文具盒', price: 19.9, stock: 1200, sales: 5670, status: 'active' },
                  { name: '学生护眼台灯LED', price: 129.0, stock: 0, sales: 890, status: 'soldout' },
                  { name: '新款钢笔套装（审核中）', price: 88.0, stock: 200, sales: 0, status: 'reviewing' },
                ].map(p => (
                  <div key={p.name} className="flex items-center gap-4 px-6 py-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">库存 {p.stock} 件 · 已售 {p.sales} 件</p>
                    </div>
                    <p className="text-sm font-bold text-red-600 whitespace-nowrap">¥{p.price}</p>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                      p.status === 'active' ? 'bg-green-100 text-green-700' :
                      p.status === 'soldout' ? 'bg-gray-100 text-gray-500' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {p.status === 'active' ? '销售中' : p.status === 'soldout' ? '已售罄' : '审核中'}
                    </span>
                    <button className="text-xs text-gray-400 hover:text-red-600 cursor-pointer whitespace-nowrap transition-colors">
                      编辑
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-black text-gray-900">订单管理</h3>
                <p className="text-xs text-gray-400 mt-1">点击「填写快递单号」为待发货订单录入物流信息</p>
              </div>
              <div className="divide-y divide-gray-50">
                {orders.map(order => (
                  <div key={order.id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-400 font-mono">{order.id}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusLabel[order.status].color}`}>
                            {statusLabel[order.status].label}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{order.product}</p>
                        <p className="text-xs text-gray-400 mt-0.5">买家：{order.buyer} · {order.time}</p>
                        {order.trackingNo && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <i className="ri-truck-line text-emerald-500 text-xs"></i>
                            <span className="text-xs text-emerald-600 font-mono font-semibold">{order.trackingNo}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <p className="text-base font-black text-red-600">¥{order.amount}</p>
                        {order.status === 'pending' && (
                          <button
                            onClick={() => setShipOrderId(order.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                          >
                            <i className="ri-truck-line"></i>填写快递单号
                          </button>
                        )}
                        {order.status === 'shipped' && (
                          <span className="text-xs text-blue-500 flex items-center gap-1">
                            <i className="ri-time-line"></i>等待买家确认
                          </span>
                        )}
                        {order.status === 'delivered' && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <i className="ri-checkbox-circle-line"></i>交易完成
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Withdraw Tab */}
          {activeTab === 'withdraw' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-black text-gray-900 mb-2">提现</h3>
                <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                  <p className="text-xs text-emerald-600 mb-1">可提现余额</p>
                  <p className="text-3xl font-black text-emerald-700">¥{sellerMockData.pendingWithdraw.toLocaleString()}</p>
                  <p className="text-xs text-emerald-500 mt-1">平台收取5%服务费，实际到账 ¥{(sellerMockData.pendingWithdraw * 0.95).toFixed(2)}</p>
                </div>

                {withdrawSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-circle-fill text-green-600"></i>
                    <span className="text-green-700 text-sm font-semibold">提现申请已提交！1-3个工作日到账</span>
                  </div>
                )}

                <div className="mb-5">
                  <p className="text-sm font-semibold text-gray-700 mb-3">提现方式</p>
                  <div className="grid grid-cols-3 gap-3">
                    {([
                      { id: 'wechat', label: '微信', icon: 'ri-wechat-pay-line', color: 'text-green-600' },
                      { id: 'alipay', label: '支付宝', icon: 'ri-alipay-line', color: 'text-blue-500' },
                      { id: 'bank', label: '银行卡', icon: 'ri-bank-card-line', color: 'text-gray-700' },
                    ] as { id: 'wechat' | 'alipay' | 'bank'; label: string; icon: string; color: string }[]).map(m => (
                      <button
                        key={m.id}
                        onClick={() => setWithdrawMethod(m.id)}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                          withdrawMethod === m.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <i className={`${m.icon} text-xl ${m.color}`}></i>
                        <span className="text-xs font-semibold text-gray-700">{m.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-5">
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">提现金额</label>
                  <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                    <span className="text-gray-400 text-sm">¥</span>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={e => setWithdrawAmount(e.target.value)}
                      placeholder="请输入提现金额"
                      className="flex-1 outline-none text-sm text-gray-900"
                    />
                    <button
                      onClick={() => setWithdrawAmount(String(sellerMockData.pendingWithdraw))}
                      className="text-xs text-red-600 font-semibold cursor-pointer whitespace-nowrap hover:text-red-700"
                    >
                      全部提现
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">最低提现金额 ¥100</p>
                </div>

                {withdrawMethod === 'bank' && (
                  <div className="mb-5 space-y-3">
                    <input type="text" placeholder="银行卡号" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                    <input type="text" placeholder="开户行（如：中国工商银行）" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                    <input type="text" placeholder="持卡人姓名" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                  </div>
                )}

                <button
                  onClick={handleWithdraw}
                  className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  申请提现
                </button>
              </div>

              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-black text-gray-900 mb-5">提现记录</h3>
                <div className="flex flex-col gap-3">
                  {[
                    { amount: 5000, method: '微信', status: 'success', time: '2025-03-28', fee: 250 },
                    { amount: 8000, method: '支付宝', status: 'success', time: '2025-03-15', fee: 400 },
                    { amount: 3200, method: '银行卡', status: 'success', time: '2025-03-01', fee: 160 },
                    { amount: 6500, method: '微信', status: 'success', time: '2025-02-18', fee: 325 },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">¥{r.amount.toLocaleString()} → {r.method}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{r.time} · 手续费 ¥{r.fee}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-600">+¥{(r.amount - r.fee).toLocaleString()}</p>
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">已到账</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showUpload && <UploadProductModal onClose={() => setShowUpload(false)} />}
      {shipOrderId && (
        <ShipOrderModal
          orderId={shipOrderId}
          onClose={() => setShipOrderId(null)}
          onConfirm={handleShip}
        />
      )}

      <Footer />
    </div>
  );
}
